import React, { useState } from "react";
import lbBadge from "../../../utilities/images/engage/lb_badge.png";
import { ReactComponent as LbTrophyIcon } from "../../../utilities/images/study_dashboard/lb_trophy_icon.svg";
import leaderboardNoAttemptsIcon from "../../../utilities/images/study_dashboard/empty_states/leaderboard_no_attempts.svg";
import "./DashboardLeaderboard.css";

const getInitials = (name) => {
  const clean = String(name || "").trim();
  if (!clean) return "U";
  const parts = clean.split(/\s+/).slice(0, 2);
  return parts.map((part) => part[0]?.toUpperCase() || "").join("") || "U";
};

const getXp = (row, tab) =>
  Number(tab === "daily" ? row?.today_xp ?? 0 : row?.weekly_xp ?? 0) || 0;

const getRank = (row, tab, fallback) =>
  tab === "daily"
    ? row?.daily_rank ?? row?.rank ?? fallback
    : row?.weekly_rank ?? row?.rank ?? fallback;

const hasUserAttempts = (user, tab) => {
  if (!user) return false;
  return getXp(user, tab) > 0;
};

const RankIndicator = ({ rank, variant }) => {
  if (variant === "first") {
    return (
      <div className="dashboard-lb__rank-badge dashboard-lb__rank-badge--first" aria-hidden="true">
        <LbTrophyIcon className="dashboard-lb__rank-trophy" aria-hidden="true" />
      </div>
    );
  }

  if (variant === "second" || variant === "third") {
    return (
      <div
        className={`dashboard-lb__rank-badge dashboard-lb__rank-badge--${variant}`}
        aria-hidden="true"
      >
        <span>{rank}</span>
      </div>
    );
  }

  return <span className="dashboard-lb__rank-text">{rank}</span>;
};

const LeaderboardRow = ({ row, tab, variant }) => {
  const rank = row?.rank ?? getRank(row, tab, "—");
  const isYou = variant === "you";

  return (
    <div className={`dashboard-lb__row dashboard-lb__row--${variant}`}>
      <div className="dashboard-lb__row-left">
        <div className="dashboard-lb__identity">
          <RankIndicator rank={rank} variant={variant} />
          <div className={`dashboard-lb__avatar${isYou ? " dashboard-lb__avatar--you" : ""}`}>
            {row?.image ? (
              <img src={row.image} alt={row?.name || "User"} />
            ) : (
              <span>{getInitials(row?.name)}</span>
            )}
          </div>
        </div>

        <div className="dashboard-lb__meta">
          <p className="dashboard-lb__name">{row?.name || "—"}</p>
          {row?.current_badge_name ? (
            <div className="dashboard-lb__badge-row">
              <img src={lbBadge} alt="" className="dashboard-lb__badge-icon" aria-hidden="true" />
              <span>{row.current_badge_name}</span>
            </div>
          ) : null}
        </div>
      </div>

      <p className="dashboard-lb__points">{getXp(row, tab)} pts</p>
    </div>
  );
};

const YourPositionEmpty = () => (
  <div className="dashboard-lb__you-empty" role="status">
    <div className="dashboard-lb__you-empty-icon-wrap" aria-hidden="true">
      <img
        src={leaderboardNoAttemptsIcon}
        alt=""
        className="dashboard-lb__you-empty-icon"
      />
    </div>
    <div className="dashboard-lb__you-empty-copy">
      <p className="dashboard-lb__you-empty-title">No attempts yet</p>
      <p className="dashboard-lb__you-empty-description">
        Take a test to see your ranking here
      </p>
    </div>
  </div>
);

const DashboardLeaderboard = ({ leaderboardData }) => {
  const [tab, setTab] = useState("daily");

  const activeBoard =
    tab === "daily"
      ? leaderboardData?.daily_leaderboard
      : leaderboardData?.weekly_leaderboard;

  if (!activeBoard) return null;

  const topRows = [
    { ...(activeBoard?.top_1 || {}), rank: 1 },
    { ...(activeBoard?.top_2 || {}), rank: 2 },
    { ...(activeBoard?.top_3 || {}), rank: 3 },
    ...(activeBoard?.others || []).slice(0, 3),
  ].filter((row) => row?.name || row?.user_id);

  const currentUser = activeBoard?.current_user;
  const showYouEmpty = !hasUserAttempts(currentUser, tab);

  const getRowVariant = (index, rank) => {
    if (rank === 1 || index === 0) return "first";
    if (rank === 2 || index === 1) return "second";
    if (rank === 3 || index === 2) return "third";
    return "default";
  };

  return (
    <aside className="dashboard-lb" aria-label="Leaderboard">
      <div className="dashboard-lb__header">
        <h2 className="dashboard-lb__title">Leaderboard</h2>
        <div className="dashboard-lb__tabs" role="tablist" aria-label="Leaderboard range">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "daily"}
            className={`dashboard-lb__tab${tab === "daily" ? " is-active" : ""}`}
            onClick={() => setTab("daily")}
          >
            Today
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "weekly"}
            className={`dashboard-lb__tab${tab === "weekly" ? " is-active" : ""}`}
            onClick={() => setTab("weekly")}
          >
            This Week
          </button>
        </div>
      </div>

      <div className="dashboard-lb__content">
        {topRows.length > 0 ? (
          <div className="dashboard-lb__list" role="list">
            {topRows.map((row, index) => (
              <LeaderboardRow
                key={`${row?.user_id || index}-${tab}`}
                row={row}
                tab={tab}
                variant={getRowVariant(index, row?.rank)}
              />
            ))}
          </div>
        ) : null}

        <div className="dashboard-lb__you">
          <div className="dashboard-lb__you-heading">
            <span>Your Position</span>
          </div>
          {showYouEmpty ? (
            <YourPositionEmpty />
          ) : (
            <LeaderboardRow
              row={{
                ...currentUser,
                rank: getRank(currentUser, tab, "—"),
              }}
              tab={tab}
              variant="you"
            />
          )}
        </div>
      </div>
    </aside>
  );
};

export default DashboardLeaderboard;
